import { Link } from "react-router-dom";
import { useState } from "react";
import PublicShell from "../components/PublicShell";
import { Button } from "../components/ui/button";
import { Check, X, Sparkles, ChevronDown } from "lucide-react";

const PLANS = [
  {
    name: "Starter",
    price_inr: "INR 0",
    price_usd: "USD 0",
    cadence: "First conference free",
    blurb: "For first-time secretariats running their inaugural MUN or debate.",
    cta: "Start free",
    href: "/register",
  },
  {
    name: "Growth",
    price_inr: "INR 24,999",
    price_usd: "USD 299",
    cadence: "Per conference",
    highlight: true,
    blurb: "For school & university conferences hosting up to 1500 delegates per event.",
    cta: "Choose Growth",
    href: "/register",
  },
  {
    name: "Scale",
    price_inr: "INR 4,99,000",
    price_usd: "USD 5,999",
    cadence: "Per year",
    blurb: "For multi-event circuits and national-level secretariats.",
    cta: "Talk to sales",
    href: "/demo",
  },
  {
    name: "Enterprise",
    price_inr: "Custom",
    price_usd: "Custom",
    cadence: "Annual contract",
    blurb: "For governments, ministries, UN-affiliates and global academic networks.",
    cta: "Contact us",
    href: "/contact",
  },
];

const FEATURE_MATRIX = [
  ["Events included", ["1", "Up to 5", "Unlimited", "Unlimited"]],
  ["Delegates per event", ["100", "1500", "Unlimited", "Unlimited"]],
  ["Council & country allocation engine", [true, true, true, true]],
  ["Pre-MUN LMS + quizzes", [true, true, true, true]],
  ["Live session OS (motions, votes, timers)", [true, true, true, true]],
  ["AI position-paper evaluation", [false, true, true, true]],
  ["Delegate passport, badges & leaderboard", [false, true, true, true]],
  ["AI delegate prep & chair assistant", [false, true, true, true]],
  ["White-label microsite + custom domain", [false, true, true, true]],
  ["Sponsor & partner portal", [false, false, true, true]],
  ["Automation centre (Email & WhatsApp)", [false, true, true, true]],
  ["Multi-event organisation seasons", [false, false, true, true]],
  ["SOC-2 evidence pack & DPA", [false, false, false, true]],
  ["SLA & dedicated success manager", [false, false, false, true]],
  ["On-site staffing & secretariat training", [false, false, false, true]],
];

const FAQ = [
  {
    q: "Is Diplomexa free for student-led conferences?",
    a: "Yes. The Starter tier is free forever for your first 100-delegate conference. We only ask for credit when you cross 100 paid registrations or want AI features.",
  },
  {
    q: "Can we use our own domain and branding?",
    a: "From the Growth plan onwards every organisation gets a white-label microsite with their logo, colours and a custom domain — visit diplomexa.ai/branding for the live preview.",
  },
  {
    q: "Do you support online and hybrid conferences?",
    a: "Absolutely. Every event has an offline / online / hybrid mode toggle and Google Meet & Zoom placeholders that can be flipped to real OAuth integrations on request.",
  },
  {
    q: "How is the AI evaluation different from generic LLMs?",
    a: "Our paper evaluator runs on Diplomexa's proprietary AI engine with rubrics fine-tuned by senior chairs across 200+ MUNs — every score comes with structured strengths, gaps and policy-alignment notes.",
  },
  {
    q: "Are the certificates verifiable?",
    a: "Yes. Every certificate has a unique QR code that resolves to a public verification page (diplomexa.com/verify/<code>) — universities and parents trust the link, not the PDF.",
  },
  {
    q: "Can sponsors track their ROI?",
    a: "Sponsors get a private portal with logo placement preview, lead capture forms, click-through reports and downloadable post-event ROI summaries.",
  },
  {
    q: "Is there a money-back guarantee?",
    a: "If your conference is cancelled within 14 days of going live we refund 100% of the platform fee — no questions asked.",
  },
  {
    q: "Do you support languages other than English?",
    a: "The platform copy is English-first today. Multi-language packs (हिन्दी, العربية, Français, Español) are on the public roadmap for Q3 2026.",
  },
];

export default function PricingPage() {
  return (
    <PublicShell>
      <section className="relative overflow-hidden border-b border-slate-200 bg-[#0A192F] text-white">
        <div className="absolute inset-0 opacity-25 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1556761175-5973dc0f32e7')" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A192F] via-[#0A192F]/90 to-[#0A192F]/60" />
        <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-24">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-[#D4AF37]/30 text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold mb-6">
            <Sparkles className="h-3 w-3" /> Transparent pricing — no per-delegate gotchas
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05]">
            Pricing built for <span className="gold-gradient-text font-serif italic">every</span> secretariat.
          </h1>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl">
            Start free. Pay only when you need to scale. Every plan includes the full
            event builder, LMS and live-session OS — we don't lock the basics behind a paywall.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {PLANS.map((p) => (
            <div
              key={p.name}
              className={`relative rounded-2xl p-7 border flex flex-col transition-all ${
                p.highlight
                  ? "bg-[#0A192F] text-white border-[#D4AF37] shadow-2xl"
                  : "bg-white border-slate-200 hover:-translate-y-1 hover:shadow-md"
              }`}
              data-testid={`plan-${p.name.toLowerCase()}`}
            >
              {p.highlight && (
                <div className="absolute -top-3 left-7 px-3 py-1 bg-[#D4AF37] text-[#0A192F] text-[10px] font-bold uppercase tracking-[0.18em] rounded-full">
                  Most loved
                </div>
              )}
              <p className={`text-xs uppercase tracking-[0.22em] font-bold ${p.highlight ? "text-[#D4AF37]" : "text-[#0F4C81]"}`}>
                {p.name}
              </p>
              <p className="mt-5 font-serif text-3xl">{p.price_inr}</p>
              <p className={`mt-1 text-base font-mono ${p.highlight ? "text-slate-300" : "text-slate-600"}`}>
                {p.price_usd}
              </p>
              <p className={`text-xs mt-2 ${p.highlight ? "text-slate-400" : "text-slate-500"}`}>
                {p.cadence}
              </p>
              <p className={`text-sm mt-5 leading-relaxed ${p.highlight ? "text-slate-300" : "text-slate-600"}`}>
                {p.blurb}
              </p>
              <Link to={p.href} className="mt-auto pt-8">
                <Button
                  className={`w-full ${
                    p.highlight
                      ? "bg-[#D4AF37] text-[#0A192F] hover:bg-[#B5952F]"
                      : "bg-[#0F4C81] text-white hover:bg-[#0A3860]"
                  }`}
                  data-testid={`plan-cta-${p.name.toLowerCase()}`}
                >
                  {p.cta}
                </Button>
              </Link>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#f6f8fb] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="max-w-3xl">
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
              Compare plans
            </p>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
              Pick the moat <span className="font-serif italic text-[#0F4C81]">your secretariat needs</span>.
            </h2>
          </div>
          <div className="mt-10 overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm" data-testid="pricing-matrix">
              <thead>
                <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-[0.16em] text-slate-500">
                  <th className="px-5 py-4 font-semibold">Feature</th>
                  {PLANS.map((p) => (
                    <th key={p.name} className="px-5 py-4 font-semibold">{p.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {FEATURE_MATRIX.map(([label, values]) => (
                  <tr key={label}>
                    <td className="px-5 py-3 text-slate-700 font-medium">{label}</td>
                    {values.map((v, i) => (
                      <td key={i} className="px-5 py-3 text-slate-700">
                        {typeof v === "string" ? (
                          <span className="font-semibold">{v}</span>
                        ) : v ? (
                          <Check className="h-4 w-4 text-emerald-600" />
                        ) : (
                          <X className="h-4 w-4 text-slate-300" />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 py-20">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
            FAQ
          </p>
          <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
            Answers before you ask.
          </h2>
        </div>
        <div className="mt-12 space-y-3" data-testid="pricing-faq">
          {FAQ.map((f, i) => (
            <FaqItem key={i} q={f.q} a={f.a} />
          ))}
        </div>
        <div className="mt-12 text-center">
          <p className="text-sm text-slate-500">Still unsure?</p>
          <Link to="/demo" className="inline-block mt-3">
            <Button className="bg-[#0F4C81] text-white hover:bg-[#0A3860]" data-testid="faq-demo-cta">
              Book a 20-minute demo
            </Button>
          </Link>
        </div>
      </section>
    </PublicShell>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between text-left px-5 py-4"
        data-testid={`faq-toggle-${q.slice(0, 20).replace(/\s+/g, "-")}`}
      >
        <span className="text-[15px] font-semibold text-[#0A192F]">{q}</span>
        <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 text-sm text-slate-600 leading-relaxed">{a}</div>
      )}
    </div>
  );
}
